import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { ModuleColumns } from '@/components/shell/ModuleColumns';
import { PageContainer } from '@/components/shell/PageContainer';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ContactsList } from '@/components/lists/ContactsList';
import { ContactDetailPage } from '@/pages/ContactDetailPage';
import { ProposalsPage } from '@/pages/ProposalsPage';
import { ProposalDetailPage } from '@/pages/ProposalDetailPage';
import { AdminPage } from '@/pages/AdminPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { EventFormPage } from '@/pages/EventFormPage';
import { EventDetailPage } from '@/pages/EventDetailPage';
import { EventEditPage } from '@/pages/EventEditPage';
import { DokumentePage } from '@/pages/DokumentePage';
import { BauvorhabenList } from '@/components/lists/BauvorhabenList';
import { BauvorhabenDetailPage } from '@/pages/BauvorhabenDetailPage';
import { BauvorhabenEditRoute } from '@/components/bauvorhaben/BauvorhabenEditForm';
import { RechnungenPage } from '@/components/rechnungen/RechnungenPage';
import { InvoiceDetailDrawer } from '@/components/rechnungen/InvoiceDetailDrawer';
import { GewerkeAdminPage } from '@/pages/GewerkeAdminPage';
import { ModuleDisabledPage } from '@/pages/ModuleDisabledPage';

function ProtectedRoute({
  children,
  requireAdmin = false,
  requireApprover = false,
  requireModule
}:
  {
    children: React.ReactNode;
    requireAdmin?: boolean;
    requireApprover?: boolean;
    requireModule?: string;
  }) {
  const { user: _user, isLoading, isAuthenticated, isAdmin, isApprover, hasModule } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireApprover && !isApprover) {
    return <Navigate to="/" replace />;
  }

  // Deaktiviertes Modul: Seite statt Redirect — `/` selbst ist die
  // Kontakte-Seite, ein Redirect könnte also in eine Schleife führen.
  // PageContainer füllt die Shell (Hinweiskarte zentriert, eigener Scroll).
  if (requireModule && !hasModule(requireModule)) {
    return (
      <PageContainer>
        <ModuleDisabledPage moduleKey={requireModule} />
      </PageContainer>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          {/* Dashboard = Startseite (module-unabhängig, „Was ist zu tun?") */}
          <Route index element={<PageContainer><DashboardPage /></PageContainer>} />

          {/* Kontakte: Listen-Spalte bleibt über die Detail-Navigation gemountet */}
          <Route element={
            <ProtectedRoute requireModule="kontakte">
              <ModuleColumns list={<ContactsList />} detailPatterns={['/contacts/:id']} />
            </ProtectedRoute>
          }>
            <Route path="contacts" element={null} />
            {/* Detail-Spalte (auch id === 'new' = Anlege-Modus) */}
            <Route path="contacts/:id" element={<ContactDetailPage />} />
          </Route>

          {/* Bauvorhaben */}
          <Route element={
            <ProtectedRoute requireModule="bauvorhaben">
              <ModuleColumns list={<BauvorhabenList />} detailPatterns={['/bauvorhaben/:id', '/bauvorhaben/:id/edit']} />
            </ProtectedRoute>
          }>
            <Route path="bauvorhaben" element={null} />
            {/* "/bauvorhaben/new" läuft bewusst über :id (wie /contacts/new) —
                KEIN explizites "new"-Route, sonst verliert useParams das 'new'. */}
            <Route path="bauvorhaben/:id" element={<BauvorhabenDetailPage />}>
              <Route index element={null} />
              {/* Bearbeiten = zusätzliche Spalte neben der Leseansicht */}
              <Route path="edit" element={<BauvorhabenEditRoute />} />
            </Route>
          </Route>

          {/* Rechnungen — vollbreites Grid ("verlinkte Excel"), Detail als
              Drawer (Outlet-Kind; Grid bleibt gemountet, Filter/Scroll
              überleben). "/rechnungen/new" läuft bewusst über :id
              (id === 'new' → Drawer-Hinweis, Anlegen läuft in der Tabelle). */}
          <Route element={
            <ProtectedRoute requireModule="rechnungen">
              <RechnungenPage />
            </ProtectedRoute>
          }>
            <Route path="rechnungen" element={null} />
            <Route path="rechnungen/:id" element={<InvoiceDetailDrawer />} />
          </Route>

          {/* Vollbreite Seiten (keine Listen-Spalte, Detailbereich = ganze Breite) */}
          <Route path="calendar" element={<ProtectedRoute requireModule="kalender"><PageContainer><CalendarPage /></PageContainer></ProtectedRoute>} />
          <Route path="calendar/new" element={<ProtectedRoute requireModule="kalender"><PageContainer><EventFormPage /></PageContainer></ProtectedRoute>} />
          <Route path="calendar/:id" element={<ProtectedRoute requireModule="kalender"><PageContainer><EventDetailPage /></PageContainer></ProtectedRoute>} />
          <Route path="calendar/:id/edit" element={<ProtectedRoute requireModule="kalender"><PageContainer><EventEditPage /></PageContainer></ProtectedRoute>} />
          <Route path="dokumente" element={<ProtectedRoute requireModule="dokumente"><PageContainer><DokumentePage /></PageContainer></ProtectedRoute>} />
          {/* Katalog-Verwaltung, approver+ — vollbreite Verwaltungsseite */}
          <Route path="bauvorhaben/gewerke" element={
            <ProtectedRoute requireModule="bauvorhaben" requireApprover><PageContainer><GewerkeAdminPage /></PageContainer></ProtectedRoute>
          } />
          <Route path="profile" element={<PageContainer><ProfilePage /></PageContainer>} />
          <Route path="proposals" element={<PageContainer><ProposalsPage /></PageContainer>} />
          <Route path="proposals/:id" element={<PageContainer><ProposalDetailPage /></PageContainer>} />
          <Route path="admin" element={
            <ProtectedRoute requireApprover>
              <PageContainer>
                <AdminPage />
              </PageContainer>
            </ProtectedRoute>
          } />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;